import * as THREE from 'three';
import { Mesh3D, Mesh3DAnimator } from './Mesh3D';
import { POI } from '../poi/POI';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { Scene } from '../core/Scene';
import { Vector3, Quaternion } from '../types';
import { BillboardUtils } from '../utils/BillboardUtils';

/**
 * POIShapeAnimator - Handles animations specific to POI shapes
 * (rotation, billboard, wiggle)
 */
class POIShapeAnimator extends Mesh3DAnimator {
    
    private _poiShape: POIShape;
    private _wiggleStartTime: number;

    // 🎯 PERFORMANCE: Cache animation state to avoid property reads every frame
    private _hasAnyAnimationEnabled: boolean = false;
    private _lastAnimationCheck: number = 0;
    private _animationCheckInterval: number = 1000; // Check every 1 second

    constructor(poiShape: POIShape) {
        super(poiShape);
        this._poiShape = poiShape;
        this._wiggleStartTime = performance.now();
    }
    

    /**
     * Update POI shape animations every frame
     */
    public update(deltaTime: number): void {
        // 🎯 PERFORMANCE: Periodically check if any animations are enabled
        const now = performance.now();
        if (now - this._lastAnimationCheck > this._animationCheckInterval) {
            this._updateAnimationCache();
            this._lastAnimationCheck = now;
        }
        
        // 🎯 EARLY EXIT: Skip all work if no animations are enabled
        if (!this._hasAnyAnimationEnabled) {
            return;
        }

        // Get animation properties directly from POIData (only when needed)
        const enableBillboard = this._poiShape.poi.poidata.enableBillboard.value ?? false;
        const enableRotation = this._poiShape.poi.poidata.enableRotation.value ?? false;
        const enableWiggle = this._poiShape.poi.poidata.enableWiggle.value ?? false;

        // Billboard animation (has priority over rotation)
        if (enableBillboard) {
            this._updateBillboard();
        }
        // Rotation animation (only if billboard is NOT active)
        else if (enableRotation) {
            this._updateRotation(deltaTime);
        }

        // Wiggle animation (works with both billboard and rotation)
        if (enableWiggle) {
            this._updateWiggle(deltaTime);
        }
    }

    /**
     * 🎯 PERFORMANCE: Cache whether any animations are enabled
     */
    private _updateAnimationCache(): void {
        const enableBillboard = this._poiShape.poi.poidata.enableBillboard.value ?? false;
        const enableRotation = this._poiShape.poi.poidata.enableRotation.value ?? false;
        const enableWiggle = this._poiShape.poi.poidata.enableWiggle.value ?? false;
        
        this._hasAnyAnimationEnabled = enableBillboard || enableRotation || enableWiggle;
    }

    private _updateBillboard(): void {
        // 🎯 SHARED: Use centralized billboard utility (full billboard for POI shapes)
        BillboardUtils.applyBillboardRotation(this._poiShape.transform);
    }

    private _updateRotation(deltaTime: number): void {
        const speed = this._poiShape.poi.poidata.rotateSpeed.value ?? 1.0;
        const currentRotation = this._poiShape.transform.rotation.value ?? Quaternion.identity();
        
        // Create rotation around Y-axis
        const rotationDelta = new Quaternion().setFromAxisAngle(Vector3.up(), speed * deltaTime);
        const newRotation = currentRotation.multiply(rotationDelta);
        
        this._poiShape.transform.rotation.value = newRotation;
    }

    private _updateWiggle(deltaTime: number): void {
        const speed = this._poiShape.poi.poidata.wiggleSpeed.value ?? 1.0;
        const height = this._poiShape.poi.poidata.wiggleHeight.value ?? 0.2;
        const time = (performance.now() - this._wiggleStartTime) * 0.001; // Convert to seconds
        
        const wiggleX = this._poiShape.poi.poidata.wiggleX.value ?? true;
        const wiggleY = this._poiShape.poi.poidata.wiggleY.value ?? true;
        const wiggleZ = this._poiShape.poi.poidata.wiggleZ.value ?? false;
        
        // Calculate wiggle offset
        const offsetX = wiggleX ? Math.sin(time * speed * 2) * height * 0.5 : 0;
        const offsetY = wiggleY ? Math.sin(time * speed) * height : 0;
        const offsetZ = wiggleZ ? Math.cos(time * speed * 1.5) * height * 0.3 : 0;
        
        const wiggleOffset = new Vector3(offsetX, offsetY, offsetZ);
        this._poiShape.transform.position.value = wiggleOffset;
    }
}

/**
 * POIShape - Base class for all POI shape meshes
 * Contains shape-specific functionality like animations, material management, etc.
 * 
 * Inheritance hierarchy:
 * Mesh3D (generic mesh functionality)
 *   ↓
 * POIShape (shape-specific functionality)
 *   ↓
 * BoxShape, SphereShape, etc. (concrete shapes)
 */
export abstract class POIShape extends Mesh3D {


    constructor(
        poi: POI, 
        sceneSettings?: SceneSettingsData, 
        appSettings?: any,
        geometryData: any = {}
    ) {
        super(poi, sceneSettings, appSettings, geometryData);
        
        // 🎯 CREATE: POI shape animation system
        this.shapeAnimator = new POIShapeAnimator(this);
    }

    /**
     * Override onUpdate to use POI-specific animator
     */
    protected override onUpdate(deltaTime: number): void {
        // Performance tracking
        const perfStart = performance.now();
        
       
        // Track material updates
        const materialStart = performance.now();
        this.primaryMaterial?.onUpdate(deltaTime);
        const _materialTime = performance.now() - materialStart;
        
        // Call parent update for material and transform sync, but we handle our own animations
        // This will call Mesh3D.onUpdate() but since shouldHaveShapeAnimations() returns false,
        // no generic shapeAnimator will be created or called
        super.onUpdate(deltaTime);
    }
} 